import { Temporal } from '@js-temporal/polyfill'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { CalendarData } from './types'

export function Calendar({
  month,
  selected,
  maxDay,
  minDay,
  data,
  loading,
  onMonth,
  onSelect,
  onClose,
  modal = false,
}: {
  month: string
  selected: string
  maxDay: string
  minDay?: string
  data?: CalendarData
  loading: boolean
  onMonth: (month: string) => void
  onSelect: (day: string) => void
  onClose: () => void
  modal?: boolean
}) {
  const first = Temporal.PlainDate.from(`${month}-01`)
  const offset = first.dayOfWeek % 7
  const maxDate = Temporal.PlainDate.from(maxDay)
  const firstYear = Math.min(first.year, Number(minDay?.slice(0, 4) ?? 1970))
  const years = Array.from(
    { length: maxDate.year - firstYear + 1 },
    (_, i) => firstYear + i,
  )
  const months = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
      new Date(Date.UTC(2026, i, 1)),
    ),
  )
  const cells = Array.from({ length: 42 }, (_, i) => i - offset + 1)
  return (
    <div
      className="calendar-popover"
      role={modal ? undefined : 'dialog'}
      aria-label="Choose recording date"
    >
      <div className="calendar-heading">
        <div className="calendar-selectors">
          <select
            aria-label="Calendar month"
            value={first.month}
            onChange={(event) =>
              onMonth(
                first
                  .with({ month: Number(event.target.value) })
                  .toString()
                  .slice(0, 7),
              )
            }
          >
            {months.map((name, i) => (
              <option
                key={name}
                value={i + 1}
                disabled={first.year === maxDate.year && i + 1 > maxDate.month}
              >
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Calendar year"
            value={first.year}
            onChange={(event) => {
              const year = Number(event.target.value)
              onMonth(
                first
                  .with({
                    year,
                    month:
                      year === maxDate.year
                        ? Math.min(first.month, maxDate.month)
                        : first.month,
                  })
                  .toString()
                  .slice(0, 7),
              )
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="calendar-navigation">
          <button
            aria-label="Previous month"
            onClick={() => onMonth(first.subtract({ months: 1 }).toString().slice(0, 7))}
          >
            <ChevronLeft size={17} />
          </button>
          <button
            aria-label="Next month"
            disabled={month >= maxDay.slice(0, 7)}
            onClick={() => onMonth(first.add({ months: 1 }).toString().slice(0, 7))}
          >
            <ChevronRight size={17} />
          </button>
          <button aria-label="Close calendar" onClick={onClose}>
            <X size={17} />
          </button>
        </div>
      </div>
      <div className="calendar-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="weekday">
            {d}
          </span>
        ))}
        {cells.map((n, i) => {
          if (n < 1 || n > first.daysInMonth) return <span key={i} />
          const day = `${month}-${String(n).padStart(2, '0')}`,
            available = data?.days[day]
          return (
            <button
              key={i}
              disabled={!available || loading || day > maxDay}
              className={day === selected ? 'selected' : ''}
              aria-label={`${day}${available ? ', recordings available' : ', no recordings'}`}
              aria-pressed={day === selected}
              onClick={() => onSelect(day)}
            >
              {n}
              {available && <i />}
            </button>
          )
        })}
      </div>
      <p>
        {loading ? 'Loading recording dates…' : 'Highlighted dates have indexed footage.'}
      </p>
    </div>
  )
}
